import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  IconButton,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import {
  createAutomationRuleApi,
  updateAutomationRuleApi,
} from "../../../api/automation.api";
import { getTemplatesApi } from "../../../api/chat.api";
import { SUGGESTED_TEMPLATES } from "../../../constants/automationEvents";
import BasicInfoStep from "./BasicInfoStep";
import TriggerStep from "./TriggerStep";
import TemplateStep from "./TemplateStep";
import SettingsStep from "./SettingsStep";

const steps = ["Basic Info", "Trigger", "Template", "Settings"];

/* Custom Step Icon */
const CustomStepIcon = ({ active, completed, icon }) => {
  if (completed) {
    return (
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          bgcolor: "primary.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CheckIcon sx={{ color: "white", fontSize: 16 }} />
      </Box>
    );
  }
  if (active) {
    return (
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          bgcolor: "primary.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{ color: "white", fontSize: 13, fontWeight: 600, lineHeight: 1 }}
        >
          {icon}
        </Typography>
      </Box>
    );
  }
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        border: "2px solid",
        borderColor: "grey.400",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography
        sx={{ color: "grey.500", fontSize: 13, fontWeight: 500, lineHeight: 1 }}
      >
        {icon}
      </Typography>
    </Box>
  );
};

const INITIAL_FORM_DATA = {
  name: "",
  description: "",
  trigger: {
    event: "",
    conditions: [],
  },
  action: {
    channel: {
      type: "whatsapp",
      whatsapp: {
        templateName: "",
        languageCode: "en_US",
      },
    },
    parameters: [],
  },
  settings: {
    enabled: true,
    delayMinutes: 0,
    retryOnFailure: false,
    maxRetries: 3,
    sendBetweenHours: {
      enabled: false,
      start: "09:00",
      end: "18:00",
    },
  },
};

const CreateRuleModal = ({ open, onClose, editRule }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [previewVariables, setPreviewVariables] = useState([]);

  useEffect(() => {
    if (open) {
      fetchTemplates();
      if (editRule) {
        setFormData(editRule);
      } else {
        setFormData(INITIAL_FORM_DATA);
        setError(null);
      }
      setActiveStep(0);
    }
  }, [open, editRule]);

  useEffect(() => {
    if (formData.action.channel.whatsapp?.templateName && templates.length > 0) {
      const template = templates.find(
        (t) => t.name === formData.action.channel.whatsapp.templateName
      );
      if (template) {
        setPreviewVariables(template.body.variables || []);
      }
    }
  }, [formData.action.channel.whatsapp?.templateName, templates]);

  const fetchTemplates = async () => {
    try {
      const response = await getTemplatesApi();
      setTemplates(response?.data?.data?.templates || []);
    } catch (err) {
      console.error("Error fetching templates:", err);
    }
  };

  /* ── Handler Functions ── */

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTriggerChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      trigger: { ...prev.trigger, [field]: value },
    }));
    if (field === "event" && SUGGESTED_TEMPLATES[value]) {
      setFormData((prev) => ({
        ...prev,
        action: {
          ...prev.action,
          channel: {
            ...prev.action.channel,
            whatsapp: {
              ...prev.action.channel.whatsapp,
              templateName: SUGGESTED_TEMPLATES[value],
            },
          },
        },
      }));
    }
  };

  const handleAddCondition = () => {
    setFormData((prev) => ({
      ...prev,
      trigger: {
        ...prev.trigger,
        conditions: [
          ...prev.trigger.conditions,
          { field: "", operator: "eq", value: "" },
        ],
      },
    }));
  };

  const handleConditionChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      trigger: {
        ...prev.trigger,
        conditions: prev.trigger.conditions.map((cond, i) =>
          i === index ? { ...cond, [field]: value } : cond
        ),
      },
    }));
  };

  const handleRemoveCondition = (index) => {
    setFormData((prev) => ({
      ...prev,
      trigger: {
        ...prev.trigger,
        conditions: prev.trigger.conditions.filter((_, i) => i !== index),
      },
    }));
  };

  const handleTemplateChange = (templateName) => {
    setFormData((prev) => ({
      ...prev,
      action: {
        ...prev.action,
        channel: {
          ...prev.action.channel,
          whatsapp: {
            ...prev.action.channel.whatsapp,
            templateName,
          },
        },
      },
    }));
  };

  const handleAddParameter = () => {
    const nextIndex = formData.action.parameters.length;
    setFormData((prev) => ({
      ...prev,
      action: {
        ...prev.action,
        parameters: [
          ...prev.action.parameters,
          {
            templateVariable: `{{${nextIndex + 1}}}`,
            leadField: "",
            defaultValue: "",
            transform: "",
          },
        ],
      },
    }));
  };

  const handleParameterChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      action: {
        ...prev.action,
        parameters: prev.action.parameters.map((param, i) =>
          i === index ? { ...param, [field]: value } : param
        ),
      },
    }));
  };

  const handleRemoveParameter = (index) => {
    setFormData((prev) => ({
      ...prev,
      action: {
        ...prev.action,
        parameters: prev.action.parameters.filter((_, i) => i !== index),
      },
    }));
  };

  const handleSettingsChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      settings: { ...prev.settings, [field]: value },
    }));
  };

  /* ── Validation ── */

  const validateStep = () => {
    switch (activeStep) {
      case 0:
        if (!formData.name.trim()) {
          setError("Rule name is required");
          return false;
        }
        return true;
      case 1:
        if (!formData.trigger.event) {
          setError("Please select a trigger event");
          return false;
        }
        return true;
      case 2:
        if (!formData.action.channel.whatsapp?.templateName) {
          setError("Please select a template");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!validateStep()) return;
    setError(null);
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    try {
      setLoading(true);
      setError(null);
      if (editRule) {
        await updateAutomationRuleApi(editRule._id, formData);
      } else {
        await createAutomationRuleApi(formData);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save automation rule");
    } finally {
      setLoading(false);
    }
  };

  /* ── Step Content Renderer ── */

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <BasicInfoStep formData={formData} onInputChange={handleInputChange} />
        );
      case 1:
        return (
          <TriggerStep
            formData={formData}
            onTriggerChange={handleTriggerChange}
          />
        );
      case 2:
        return (
          <TemplateStep
            formData={formData}
            templates={templates}
            previewVariables={previewVariables}
            onTemplateChange={handleTemplateChange}
            onAddParameter={handleAddParameter}
            onParameterChange={handleParameterChange}
            onRemoveParameter={handleRemoveParameter}
          />
        );
      case 3:
        return (
          <SettingsStep
            formData={formData}
            onSettingsChange={handleSettingsChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxWidth: 780,
        },
      }}
    >
      {/* Title */}
      <Box sx={{ pt: 2.5, px: 3, pb: 0 }}>
        <Box display="flex" alignItems="center" gap={1}>
          {activeStep > 0 && (
            <IconButton onClick={handleBack} size="small" sx={{ mr: 0.5 }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{ fontSize: "1.15rem" }}
          >
            {editRule ? "Edit Automation Rule" : "Create Automation Rule"}
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
        {/* Stepper */}
        <Stepper
          activeStep={activeStep}
          sx={{
            mb: 1,
            mt: 0.5,
            "& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line": {
              borderColor: "primary.main",
              borderTopStyle: "solid",
            },
            "& .MuiStepConnector-root.Mui-active .MuiStepConnector-line": {
              borderColor: "primary.main",
              borderTopStyle: "solid",
            },
            "& .MuiStepConnector-line": {
              borderColor: "grey.300",
              borderTopStyle: "dashed",
              borderTopWidth: 2,
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel
                StepIconComponent={CustomStepIcon}
                sx={{
                  "& .MuiStepLabel-label": {
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    mt: 0.5,
                  },
                  "& .MuiStepLabel-label.Mui-active": {
                    color: "primary.main",
                    fontWeight: 600,
                  },
                  "& .MuiStepLabel-label.Mui-completed": {
                    color: "text.primary",
                    fontWeight: 500,
                  },
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: 1.5, fontSize: "0.82rem" }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            textTransform: "uppercase",
            fontWeight: 600,
            color: "text.secondary",
            fontSize: "0.82rem",
          }}
        >
          Cancel
        </Button>
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            sx={{
              textTransform: "uppercase",
              fontWeight: 600,
              borderRadius: 2,
              px: 4,
              py: 1,
              fontSize: "0.82rem",
            }}
          >
            Next Step
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
            sx={{
              textTransform: "uppercase",
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              py: 1,
              fontSize: "0.82rem",
            }}
          >
            {editRule ? "Update Rule" : "Create Rule"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateRuleModal;
